import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DATABASE_API_URL,
  STOCK_SEARCH_API_URL,
  BACKEND_API_URL,
  LOGIN_API_URL,
} from "../apiConfig";
import { checkUpstoxStatus } from "../services/upstoxAuth";

const SERVICES = [
  { key: "DATABASE_API_URL", name: "Database", url: DATABASE_API_URL, type: "endpoint" },
  { key: "STOCK_SEARCH_API_URL", name: "Stock Search", url: STOCK_SEARCH_API_URL, type: "endpoint" },
  { key: "BACKEND_API_URL", name: "Backend API", url: BACKEND_API_URL, type: "endpoint" },
  { key: "LOGIN_API_URL", name: "Login API", url: LOGIN_API_URL, type: "endpoint" },
];



const getStatusLabel = ({ status, error }) => {
  if (status === "checking") return "Checking...";
  if (status === "live") return "Live";
  if (status === "expired") return "Expired";
  if (status === "sleeping") return "Sleeping";
  if (status === "error") return error || "Unavailable";
  return "Unknown";
};

const getStatusClass = (status) => {
  if (status === "live") return "bg-emerald-500/15 text-emerald-200 border-emerald-400";
  if (status === "expired") return "bg-amber-500/10 text-amber-200 border-amber-400";
  if (status === "sleeping") return "bg-amber-500/10 text-amber-200 border-amber-400";
  if (status === "error") return "bg-red-500/10 text-red-200 border-red-400";
  
  return "bg-slate-500/10 text-slate-200 border-slate-400";
};

const ServiceStatusFooter = () => {
  const initialStatuses = useMemo(
    () =>
      SERVICES.reduce((acc, service) => {
        acc[service.key] = { status: "checking", statusCode: null, error: null };
        return acc;
      }, {}),
    []
  );

  const [statuses, setStatuses] = useState(initialStatuses);
  const isCheckingRef = useRef(false);
  const [lastChecked, setLastChecked] = useState(null);

  const anyError = useMemo(
    () => Object.values(statuses).some((item) => item.status === "error"),
    [statuses]
  );

  const fetchServiceStatus = useCallback(async (service) => {
    if (service.type === "token") {
      try {
        const tokenStatus = await checkUpstoxStatus();
        return {
          status: tokenStatus.authenticated ? "live" : "expired",
          statusCode: null,
          error: tokenStatus.authenticated ? null : tokenStatus.message || "Token expired or unavailable",
        };
      } catch (error) {
        return {
          status: "error",
          statusCode: null,
          error: error?.message || "Unable to check token status",
        };
      }
    }

    // Probe the base URL directly. Querying /health or /api/health can trigger adblockers (ERR_BLOCKED_BY_CLIENT)
    // and logs noisy 404 console errors for services that do not define those specific endpoints.
    const base = service.url.replace(/\/$/, '');
    const candidates = [`${base}/`];
    let lastError = null;
    let lastStatus = null;

    for (const candidate of candidates) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        const response = await fetch(candidate, {
          method: 'GET',
          cache: 'no-store',
          mode: 'cors',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        lastStatus = response.status;

        if (response.status === 401) {
          return { status: 'expired', statusCode: 401, error: null, attemptedUrl: candidate };
        }

        if (response.ok) {
          return { status: 'live', statusCode: response.status, error: null, attemptedUrl: candidate };
        }

        // If 404 on this candidate, try the next candidate; otherwise return error with code
        if (response.status === 404) {
          lastError = `HTTP 404 at ${candidate}`;
          continue;
        }

        return { status: 'error', statusCode: response.status, error: `HTTP ${response.status}`, attemptedUrl: candidate };
      } catch (err) {
        // network or timeout for this candidate — remember error and try next
        lastError = err?.message || String(err);
        continue;
      }
    }

    if (lastStatus === 404) {
      // Treat 404 as live to avoid noisy warnings for services that return HTML 404 on base paths
      return { status: 'live', statusCode: 404, error: null, attemptedUrl: candidates[candidates.length - 1] };
    }

    return { status: 'error', statusCode: lastStatus, error: lastError || 'No response', attemptedUrl: candidates[candidates.length - 1] };
  }, []);

  const checkAllServices = useCallback(async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    const nextStatuses = {};

    await Promise.all(
      SERVICES.map(async (service) => {
        const statusResult = await fetchServiceStatus(service);
        nextStatuses[service.key] = statusResult;
      })
    );

    setStatuses(nextStatuses);
    setLastChecked(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    isCheckingRef.current = false;
  }, [fetchServiceStatus]);

  useEffect(() => {
    checkAllServices();

    // Refresh service health every 5 minutes to avoid hitting free-tier limits
    const intervalId = setInterval(() => {
      checkAllServices();
    }, 300000); // 300000 ms = 5 minutes

    return () => clearInterval(intervalId);
  }, [checkAllServices]);

  return (
    <footer className="bg-slate-950/95 border-t border-slate-800 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 w-full">
            {SERVICES.map((service) => {
              const serviceStatus = statuses[service.key] || { status: "checking", statusCode: null, error: null };
              return (
                <div key={service.key} className="rounded-2xl border p-3 border-slate-700 bg-slate-900/70">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-200">{service.name}</p>
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getStatusClass(serviceStatus.status)}`}>
                      {getStatusLabel(serviceStatus)}
                    </span>
                  </div>
                  {service.url && (
                        <p className="mt-2 text-xs text-slate-400 truncate">{service.url}</p>
                  )}
                      {serviceStatus.statusCode != null && (
                        <p className="mt-2 text-[11px] text-slate-500">HTTP {serviceStatus.statusCode}</p>
                      )}
                      {serviceStatus.attemptedUrl && serviceStatus.status === 'error' && (
                        <p className="mt-1 text-[11px] text-slate-500 truncate">Tried: {serviceStatus.attemptedUrl}</p>
                      )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <div className="text-slate-400 text-xs">
              {lastChecked ? `Last checked at ${lastChecked}` : "Checking endpoints..."}
            </div>
          </div>
        </div>
        {anyError && (
          <p className="mt-3 text-xs text-amber-300">
            Some services returned a network error. Status will refresh automatically every 5 minutes.
          </p>
        )}
      </div>
    </footer>
  );
};

export default ServiceStatusFooter;
